import { Injectable } from '@angular/core';
import { ComponentBase } from '../component-base';
import { StageService } from './stage.service';
import { SceneUtilsService } from './scene-utils.service';
import { RailingGenerationService } from './railing/services/railing-generation.service';
import { SizeHelperService } from './size-helper.service';
import { AutoScaleSprite } from '../custom/AutoScaleSprite';

declare let THREE: any;

// DockRail side anchors (spec pp.13-15): on the railing step a dock starts
// with no railing and an anchor icon at the midpoint of every free side.
// Clicking an anchor clears that side's noRail flag (seeded on dock template
// points at project creation) and builds the railing run on that side; sides
// that have a railing run show no anchor. A side whose run was dragged away
// entirely by the RailLengthModifierService gets its anchor back.
@Injectable()
export class DockRailAnchorService extends ComponentBase {
  private sprites = [];
  private loader = new THREE.TextureLoader();
  private raycaster = new THREE.Raycaster();
  private buildSubscription: any = null;
  // loaded once — drawSprites runs on every railing rebuild and THREE.Cache is off
  private spriteMap: any = null;
  private suppressNextMouseUp = false;

  constructor(
    private stage: StageService,
    private sceneUtils: SceneUtilsService,
    private railingGenerationService: RailingGenerationService,
    private sizeHelper: SizeHelperService
  ) {
    super();
    this.disable();
  }

  enable() {
    super.enable();
    this.drawSprites();
    if (!this.buildSubscription || this.buildSubscription.closed) {
      this.buildSubscription = this.railingGenerationService.build.subscribe(() => {
        if (this.isEnabled()) {
          this.drawSprites();
        }
      });
    }
  }

  disable() {
    this.removeSprites();
    if (this.buildSubscription) {
      this.buildSubscription.unsubscribe();
      this.buildSubscription = null;
    }
    super.disable();
  }

  private getMainFloor() {
    const compound = this.stage.getCompoundFloor();
    if (!compound || !compound.children || !compound.children.length) {
      return null;
    }
    return compound.children.find(f => f.userData.mainFloor) || compound.children[0];
  }

  // positive = counter-clockwise in the floor's x/y (world x/z) plane
  private polygonSignedArea(points): number {
    let area = 0;
    for (let i = 0; i < points.length; i++) {
      const p = points[i];
      const n = points[(i + 1) % points.length];
      area += p.x * n.y - n.x * p.y;
    }
    return area / 2;
  }

  // an anchor is shown on every side that currently has no railing run
  private sideNeedsAnchor(points, i): boolean {
    const point = points[i];
    if (point.superstructureType) {
      return false;
    }
    if (point.noRail) {
      return true;
    }
    if (!point.railStart && !point.railStop) {
      return false;
    }
    const next = points[(i + 1) % points.length];
    const length = Math.sqrt(
      Math.pow(next.x - point.x, 2) + Math.pow(next.y - point.y, 2)
    );
    const minRun = this.sizeHelper.INCHToCM(60);
    return length - (point.railStart || 0) - (point.railStop || 0) < minRun;
  }

  drawSprites(): void {
    this.removeSprites();
    const floor = this.getMainFloor();
    if (!floor || !floor.userData.points) {
      return;
    }

    const height = floor.getWorldPosition(new THREE.Vector3()).y + 5;
    const points = floor.userData.points;
    const ccw = this.polygonSignedArea(points) > 0;
    if (!this.spriteMap) {
      this.spriteMap = this.loader.load('../../assets/images/sprites/dock_anchor.png');
    }
    const offset = 45; // cm outward from the edge midpoint

    for (let i = 0; i < points.length; i++) {
      if (!this.sideNeedsAnchor(points, i)) {
        continue;
      }
      const fPoint = points[i];
      const sPoint = points[(i + 1) % points.length];

      const dir = new THREE.Vector3(sPoint.x - fPoint.x, 0, sPoint.y - fPoint.y).normalize();
      // outward normal of the edge, valid for concave outlines too
      const outward = ccw
        ? new THREE.Vector3(dir.z, 0, -dir.x)
        : new THREE.Vector3(-dir.z, 0, dir.x);

      const spriteMaterial = new THREE.SpriteMaterial({
        map: this.spriteMap,
        color: 0xffffff,
        depthTest: false,
        depthWrite: false
      });
      const sprite = new AutoScaleSprite(44, 44, spriteMaterial);
      sprite.position.set(
        (fPoint.x + sPoint.x) / 2 + outward.x * offset,
        height,
        (fPoint.y + sPoint.y) / 2 + outward.z * offset
      );
      sprite.name = 'Dock rail anchor';
      sprite.userData.floor = floor;
      sprite.userData.pointIndex = i;

      this.sprites.push(sprite);
      this.stage.add(sprite);
    }
  }

  removeSprites() {
    if (this.sprites.length) {
      this.stage.removeMany(this.sprites);
      this.sprites = [];
    }
  }

  mouseDown(e: MouseEvent) {
    if (!this.sprites.length) {
      return;
    }
    const p = this.sceneUtils.getRay(e.offsetX, e.offsetY);
    this.raycaster.ray.origin.copy(p.ray.origin);
    this.raycaster.ray.direction.copy(p.ray.direction);

    const intersection = this.raycaster.intersectObjects(this.sprites);
    if (intersection.length) {
      const { floor, pointIndex } = intersection[0].object.userData;
      const point = floor.userData.points[pointIndex];
      point.noRail = false;
      point.railStart = 0;
      point.railStop = 0;
      // clicking an anchor is an explicit "add railing here" — always show it
      this.railingGenerationService.buildRailing(true);
      this.drawSprites();
      this.suppressNextMouseUp = true;
      return false;
    }
  }

  // swallow the matching mouseUp too, otherwise Select3dService's release
  // handler still runs and drops the current tier selection
  mouseUp() {
    if (this.suppressNextMouseUp) {
      this.suppressNextMouseUp = false;
      return false;
    }
  }
}



// WEBPACK FOOTER //
// ./src/app/services/dock-rail-anchor.service.ts